import { useEffect, useCallback, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
import queryString from "query-string";
import useFilterStore from "../store/filterStore";

/**
 * Custom hook for filter management with URL query parameter syncing
 * 
 * IMPORTANT BEHAVIOR:
 * - Filters persist ONLY in runtime memory (current session)
 * - Manual page refresh resets filters to initial values AND clears URL params
 * - Filters are retained during auto-refresh after CRUD operations
 * - URL query params are synced bidirectionally during session
 * 
 * @param {string} filterType - "invoice" or "assignment"
 * @param {object} initialFilters - default filter structure
 * @returns {object} { filters, setFilters, updateFilter, clearFilters, hasActiveFilters }
 */
export const useFilterPersistence = (filterType, initialFilters) => {
  const location = useLocation();
  const history = useHistory();
  const isFirstMountRef = useRef(true);

  // Call hooks unconditionally (required by React)
  const invoiceFilters = useFilterStore((state) => state.invoiceFilters);
  const assignmentFilters = useFilterStore((state) => state.assignmentFilters);

  // Select filters based on filterType
  const filters = filterType === "invoice" ? invoiceFilters : assignmentFilters;

  // Set filters in store
  const setStoredFilters = useCallback(
    (newFilters) => {
      if (filterType === "invoice") {
        useFilterStore.setState({ invoiceFilters: newFilters });
      } else {
        useFilterStore.setState({ assignmentFilters: newFilters });
      }
    },
    [filterType]
  );

  // Update single filter in store
  const updateStoredFilter = useCallback(
    (key, value) => {
      if (filterType === "invoice") {
        useFilterStore.setState((state) => ({
          invoiceFilters: {
            ...state.invoiceFilters,
            [key]: value,
          },
        }));
      } else {
        useFilterStore.setState((state) => ({
          assignmentFilters: {
            ...state.assignmentFilters,
            [key]: value,
          },
        }));
      }
    },
    [filterType]
  );

  // Clear all filters in store
  const clearStoredFilters = useCallback(() => {
    if (filterType === "invoice") {
      useFilterStore.setState({ invoiceFilters: initialFilters });
    } else {
      useFilterStore.setState({ assignmentFilters: initialFilters });
    }
  }, [filterType, initialFilters]);

  // Initialize filters and clear URL params on component mount (page refresh scenario)
  useEffect(() => {
    // On first mount of this component, reset everything to clean state
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      // Reset filters to initial values
      setStoredFilters(initialFilters);
      // Clear URL query params so manual refresh doesn't re-apply old filters
      history.replace({ ...location, search: "" });
      return; // Don't process URL params on first mount
    }
  }, [initialFilters, setStoredFilters, history, location]);

  // Sync filters to URL whenever they change (after initial mount)
  useEffect(() => {
    const urlParams = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && String(value).trim() !== "") {
        urlParams[key] = value;
      }
    });

    const newSearch = queryString.stringify(urlParams);
    const currentSearch = location.search.substring(1); // Remove leading ?

    // Only update URL if it changed
    if (newSearch !== currentSearch) {
      history.replace({ ...location, search: newSearch ? `?${newSearch}` : "" });
    }
  }, [filters, location, history]);

  // Update single filter
  const updateFilter = useCallback(
    (key, value) => {
      updateStoredFilter(key, value);
    },
    [updateStoredFilter]
  );

  // Batch update multiple filters
  const updateFilters = useCallback(
    (newFilters) => {
      setStoredFilters({ ...filters, ...newFilters });
    },
    [filters, setStoredFilters]
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    clearStoredFilters();
  }, [clearStoredFilters]);

  // Check if any filter is active
  const hasActiveFilters = Object.values(filters || {}).some(
    (value) => value !== null && value !== undefined && String(value).trim() !== ""
  );

  return {
    filters,
    setFilters: updateFilters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
  };
};

export default useFilterPersistence;
